// react
import { useCallback, useEffect, useState } from 'react';

// msal
import { useMsal } from '@azure/msal-react';

// types
import { V2ContractPayload, V2Contract } from '../@types';

// services
import {
	createV2Contract,
	deactivateV2Contract,
	deleteV2Contract,
	fetchContract,
	fetchContracts,
	updateV2Contract,
	ApiResponse,
	QueryParams,
} from '../services/api';

// Types

export interface UseContractsParams {
	activityId?: number;
	isEnabled?: boolean;
	projectId?: number;
	query?: QueryParams;
}

export interface UseContractsReturn {
	create: (payload: V2ContractPayload) => Promise<ApiResponse<V2Contract>>;
	data: V2Contract[];
	deactivate: (id: number) => Promise<ApiResponse<V2Contract>>;
	delete: (id: number) => Promise<ApiResponse<{ deleted: true }>>;
	error: string | null;
	loading: boolean;
	pagination: ApiResponse<V2Contract[]>['pagination'];
	refetch: () => Promise<void>;
	update: (id: number, payload: Partial<V2ContractPayload>) => Promise<ApiResponse<V2Contract>>;
}

// Hook

export const useV2Contracts = ({
	activityId,
	isEnabled = true,
	projectId,
	query = {},
}: UseContractsParams = {}): UseContractsReturn => {
	// auth
	const accessToken = sessionStorage.getItem('accessToken');
	const idToken = sessionStorage.getItem('idToken');
	const { accounts, instance } = useMsal();

	const refreshTokens = useCallback(async () => {
		if (accounts.length === 0) return null;
		try {
			const result = await instance.acquireTokenSilent({
				account: accounts[0],
				scopes: ['user.read'],
			});
			if (result.accessToken && result.idToken) {
				sessionStorage.setItem('accessToken', result.accessToken);
				sessionStorage.setItem('idToken', result.idToken);
				return { accessToken: result.accessToken, idToken: result.idToken };
			}
		} catch {
			// fall through to sessionStorage tokens
		}
		return null;
	}, [accounts, instance]);

	// state
	const [data, setData] = useState<V2Contract[]>([]);
	const [error, setError] = useState<string | null>(null);
	const [loading, setLoading] = useState(false);
	const [pagination, setPagination] = useState<ApiResponse<V2Contract[]>['pagination']>(undefined);

	// fetch data
	// eslint-disable-next-line react-hooks/exhaustive-deps
	const queryKey = JSON.stringify(query);

	const fetchData = useCallback(async () => {
		if (!accessToken || !idToken) {
			return;
		}

		setLoading(true);
		setError(null);

		try {
			const res = await fetchContracts(
				{ accessToken, idToken },
				{ ...query, activity: activityId, project: projectId }
			);

			if (res.error) {
				setError(res.error.message);
			} else {
				setData(res.data || []);
				setPagination(res.pagination);
			}
		} catch (err) {
			setError(err instanceof Error ? err.message : 'Failed to fetch contracts');
		} finally {
			setLoading(false);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [accessToken, idToken, activityId, projectId, queryKey]);

	// initial fetch
	useEffect(() => {
		if (isEnabled && projectId) {
			fetchData();
		}
	}, [isEnabled, fetchData, projectId]);

	// create contract
	const create = useCallback(
		async (payload: V2ContractPayload): Promise<ApiResponse<V2Contract>> => {
			const tokens =
				(await refreshTokens()) || (accessToken && idToken ? { accessToken, idToken } : null);
			if (!tokens) {
				return { error: { id: 'UNAUTHORIZED', message: 'Not authenticated' }, status: 401 };
			}

			try {
				const res = await createV2Contract(tokens, payload);

				if (!res.error) {
					await fetchData();
				}

				return res;
			} catch (err) {
				const message = err instanceof Error ? err.message : 'Failed to create contract';
				return { error: { id: 'CREATE_FAILED', message }, status: 500 };
			}
		},
		[accessToken, idToken, fetchData, refreshTokens]
	);

	// update contract
	const update = useCallback(
		async (id: number, payload: Partial<V2ContractPayload>): Promise<ApiResponse<V2Contract>> => {
			const tokens =
				(await refreshTokens()) || (accessToken && idToken ? { accessToken, idToken } : null);
			if (!tokens) {
				return { error: { id: 'UNAUTHORIZED', message: 'Not authenticated' }, status: 401 };
			}

			try {
				const res = await updateV2Contract(tokens, id, payload);

				if (!res.error) {
					await fetchData();
				}

				return res;
			} catch (err) {
				const message = err instanceof Error ? err.message : 'Failed to update contract';
				return { error: { id: 'UPDATE_FAILED', message }, status: 500 };
			}
		},
		[accessToken, idToken, fetchData, refreshTokens]
	);

	// deactivate contract
	const deactivateContract = useCallback(
		async (id: number): Promise<ApiResponse<V2Contract>> => {
			const tokens =
				(await refreshTokens()) || (accessToken && idToken ? { accessToken, idToken } : null);
			if (!tokens) {
				return { error: { id: 'UNAUTHORIZED', message: 'Not authenticated' }, status: 401 };
			}

			try {
				const res = await deactivateV2Contract(tokens, id);

				if (!res.error) {
					await fetchData();
				}

				return res;
			} catch (err) {
				const message = err instanceof Error ? err.message : 'Failed to deactivate contract';
				return { error: { id: 'DEACTIVATE_FAILED', message }, status: 500 };
			}
		},
		[accessToken, idToken, fetchData, refreshTokens]
	);

	// hard-delete contract (DRAFT + unlinked only, enforced by backend guard)
	const deleteContract = useCallback(
		async (id: number): Promise<ApiResponse<{ deleted: true }>> => {
			const tokens =
				(await refreshTokens()) || (accessToken && idToken ? { accessToken, idToken } : null);
			if (!tokens) {
				return { error: { id: 'UNAUTHORIZED', message: 'Not authenticated' }, status: 401 };
			}

			try {
				const res = await deleteV2Contract(tokens, id);

				if (!res.error) {
					await fetchData();
				}

				return res;
			} catch (err) {
				const message = err instanceof Error ? err.message : 'Failed to delete contract';
				return { error: { id: 'DELETE_FAILED', message }, status: 500 };
			}
		},
		[accessToken, idToken, fetchData, refreshTokens]
	);

	return {
		create,
		data,
		deactivate: deactivateContract,
		delete: deleteContract,
		error,
		loading,
		pagination,
		refetch: fetchData,
		update,
	};
};

// Single Contract Hook

export interface UseContractParams {
	id: number;
	isEnabled?: boolean;
	query?: QueryParams;
}

export interface UseContractReturn {
	data: V2Contract | null;
	error: string | null;
	loading: boolean;
	refetch: () => Promise<void>;
	update: (payload: Partial<V2ContractPayload>) => Promise<ApiResponse<V2Contract>>;
}

export const useContract = ({
	id,
	isEnabled = true,
	query = {},
}: UseContractParams): UseContractReturn => {
	// auth
	const accessToken = sessionStorage.getItem('accessToken');
	const idToken = sessionStorage.getItem('idToken');

	// state
	const [data, setData] = useState<V2Contract | null>(null);
	const [error, setError] = useState<string | null>(null);
	const [loading, setLoading] = useState(false);

	// fetch data
	const queryKey = JSON.stringify(query);

	const fetchData = useCallback(async () => {
		if (!accessToken || !idToken || !id) {
			return;
		}

		setLoading(true);
		setError(null);

		try {
			const res = await fetchContract({ accessToken, idToken }, id, query);

			if (res.error) {
				setError(res.error.message);
			} else {
				setData(res.data || null);
			}
		} catch (err) {
			setError(err instanceof Error ? err.message : 'Failed to fetch contract');
		} finally {
			setLoading(false);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [accessToken, idToken, id, queryKey]);

	// initial fetch
	useEffect(() => {
		if (isEnabled && id) {
			fetchData();
		}
	}, [isEnabled, fetchData, id]);

	// update contract
	const update = useCallback(
		async (payload: Partial<V2ContractPayload>) => {
			if (!accessToken || !idToken) {
				return { error: { id: 'UNAUTHORIZED', message: 'Not authenticated' }, status: 401 };
			}

			const res = await updateV2Contract({ accessToken, idToken }, id, payload);

			if (!res.error) {
				await fetchData();
			}

			return res;
		},
		[accessToken, idToken, id, fetchData]
	);

	return {
		data,
		error,
		loading,
		refetch: fetchData,
		update,
	};
};
